// Manual attendance ("Add attendees", mockup markAtt()) and "View attendees" (checked-in list + CSV).
// Data per screens/AdminScreen.js loadUncheckedAttendees; mark/unmark via useManualAttendance.
// "View attendees" can also undo a MANUAL mark from an earlier session (the callable refuses QR check-ins).
import React, { useEffect, useRef, useState } from "react";
import Modal, { ModalBody } from "../../../components/Modal";
import { Avatar, Button, Empty, SkeletonRows } from "../../../components/primitives";
import Icon from "../../../components/Icon";
import { useToast } from "../../../components/Toast";
import { toDate } from "../../../lib/format";
import { dayLabel } from "../../../../../utils/eventDateParts";
import { ClubService } from "../../../../../services/clubService";
import { EventService } from "../../../../../services/eventService";
import { exportEventAttendanceCsv } from "../../../../../services/attendanceExportService";
import { msg } from "./eventUtils";
import { useManualAttendance } from "../../../../../components/admin/useManualAttendance";

export default function AttendeesModal({ event, club, mode, adminId, onClose, onCountsChanged }) {
  const toast = useToast();
  const [state, setState] = useState({ loading: true, error: "", checkedIn: [], unchecked: [] });
  const [reload, setReload] = useState(0);
  const [exporting, setExporting] = useState(false);
  const epoch = useRef(0);

  useEffect(() => {
    const my = ++epoch.current;
    setState((s) => ({ ...s, loading: true, error: "" }));
    (async () => {
      const { data: members, error: mErr } = await ClubService.getClubMembers(club.id);
      if (my !== epoch.current) return;
      if (mErr) { setState({ loading: false, error: msg(mErr, "Failed to load members"), checkedIn: [], unchecked: [] }); return; }
      const memberList = members || [];
      const knownUsersById = new Map(memberList.filter((m) => m.user?.id).map((m) => [m.user.id, m.user]));
      const { data: attendees, error: aErr } = await EventService.getEventAttendees(event.id, { knownUsersById });
      if (my !== epoch.current) return;
      if (aErr) { setState({ loading: false, error: msg(aErr, "Failed to load attendees"), checkedIn: [], unchecked: [] }); return; }
      const checkedIn = (attendees || []).filter((a) => a.checked_in).map((a) => ({ ...a.user, manual: a.check_in_method === "manual" }));
      const ids = new Set(checkedIn.map((u) => u.id));
      const unchecked = memberList.filter((m) => m.user?.id && !ids.has(m.user.id)).map((m) => m.user);
      setState({ loading: false, error: "", checkedIn, unchecked });
    })();
    return () => { epoch.current += 1; };
  }, [club.id, event.id, reload]);

  const manual = useManualAttendance({ eventId: event.id, adminId, onCountsChanged });
  const toggle = async (u) => {
    const on = manual.markedIds.has(u.id);
    const { error } = await (on ? manual.unmark(u.id) : manual.mark(u.id));
    if (error) toast(msg(error, on ? "Failed to unmark attendance" : "Failed to mark attendance"), "alert");
  };
  // View mode: rows start checked in, so track the ones undone here (the hook's markedIds starts empty).
  const [undone, setUndone] = useState(() => new Set());
  const toggleView = async (u) => {
    const wasUndone = undone.has(u.id);
    const { error } = await (wasUndone ? manual.mark(u.id) : manual.unmark(u.id));
    if (error) { toast(msg(error, wasUndone ? "Failed to mark attendance" : "Failed to unmark attendance"), "alert"); return; }
    setUndone((prev) => { const next = new Set(prev); if (wasUndone) next.delete(u.id); else next.add(u.id); return next; });
  };

  const exportCsv = async () => {
    setExporting(true);
    const { error } = await exportEventAttendanceCsv(event);
    setExporting(false);
    if (error) toast(msg(error), "alert");
  };

  const d = toDate(event.start_date);
  const adding = mode === "add";
  const list = adding ? state.unchecked : state.checkedIn;

  return (
    <Modal title={adding ? "Mark attendance" : "Attendees"} onClose={onClose} testId="attendees">
      <ModalBody>
        <p className="meta">
          {event.title}{d ? ` · ${dayLabel(d)}` : ""}.{adding ? " Tap a marked person again to undo." : ` ${state.checkedIn.length - undone.size} checked in. Manual marks can be undone here.`}
        </p>
        {!adding && (
          <Button variant="ghost" size="sm" icon="download" disabled={exporting} onClick={exportCsv} data-testid="attendees.exportCsv" style={{ alignSelf: "flex-start" }}>
            {exporting ? "Exporting…" : "Export CSV"}
          </Button>
        )}
        {state.loading ? (
          <SkeletonRows rows={4} />
        ) : state.error ? (
          <Empty icon="alert" title="Couldn't load attendees">
            <p>{state.error}</p>
            <Button variant="sec" size="sm" onClick={() => setReload((n) => n + 1)} data-testid="attendees.retry" style={{ marginTop: 10 }}>Retry</Button>
          </Empty>
        ) : list.length === 0 ? (
          <div className="card empty">{adding ? "Everyone in this club is already checked in." : "No one has checked in yet."}</div>
        ) : (
          <div className="col" role="list">
            {list.map((u) => {
              const marked = manual.markedIds.has(u.id);
              const busy = manual.markingIds.has(u.id);
              return (
                <div key={u.id} className="ev-att-row" role="listitem">
                  <Avatar name={u.full_name} url={u.avatar_url} id={u.id} size={38} />
                  <div className="grow">
                    <b>{u.full_name || "Member"}</b>
                    {u.email && <div className="meta">{u.email}</div>}
                  </div>
                  {!adding && u.manual && (
                    undone.has(u.id) ? (
                      <Button variant="sec" size="xs" disabled={busy} onClick={() => toggleView(u)} data-testid={`attendees.row.${u.id}.remark`}>Mark present</Button>
                    ) : (
                      <Button variant="ghost" size="xs" disabled={busy} onClick={() => toggleView(u)} data-testid={`attendees.row.${u.id}.unmarkSaved`}>Unmark</Button>
                    )
                  )}
                  {adding && (
                    marked ? (
                      <Button variant="ok" size="xs" disabled={busy} onClick={() => toggle(u)} data-testid={`attendees.row.${u.id}.unmark`}><Icon name="check" size={13} />Marked</Button>
                    ) : (
                      <Button variant="sec" size="xs" disabled={busy} onClick={() => toggle(u)} data-testid={`attendees.row.${u.id}.mark`}>Mark present</Button>
                    )
                  )}
                </div>
              );
            })}
          </div>
        )}
      </ModalBody>
    </Modal>
  );
}
